import { useEffect, useMemo, useState } from 'react';
import Papa from 'papaparse';
import {
  Archive,
  BookOpenCheck,
  Check,
  ChevronRight,
  Copy,
  FileUp,
  FilePlus2,
  Menu,
  MessageSquareText,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Trash2,
  WandSparkles,
} from 'lucide-react';
import {
  analyzeConversations,
  createKnowledgeItem,
  deleteKnowledgeItem,
  generateKnowledgeAnswer,
  getKnowledge,
  updateKnowledgeItem,
  type KnowledgeData,
  type KnowledgeItem,
  type KnowledgeStatus as ItemStatus,
} from '../api/client';

function statusLabel(status: ItemStatus) {
  return status === 'published' ? '已入库' : status === 'draft' ? '待确认' : '待生成';
}

function normalize(value: string) {
  return value.toLowerCase().replace(/[\s，。？！、,.?!]/g, '');
}

function matchScore(item: KnowledgeItem, keyword: string) {
  const query = normalize(keyword);
  if (!query) return item.frequency;
  const candidates = [item.question, ...item.aliases].map(normalize);
  if (candidates.some((value) => value === query)) return 1000;
  if (candidates.some((value) => value.includes(query) || query.includes(value))) return 700;
  return Math.max(...candidates.map((value) => [...new Set(query)].filter((char) => value.includes(char)).length / Math.max(query.length, 1) * 100));
}

function parseConversations(source: string) {
  const trimmed = source.trim();
  if (!trimmed) return [];
  try {
    const json = JSON.parse(trimmed) as unknown;
    if (Array.isArray(json)) {
      return json.map((value) => typeof value === 'string' ? value : JSON.stringify(value)).filter(Boolean);
    }
  } catch {
    // CSV and text exports are analyzed line by line.
  }
  return trimmed.split(/\r?\n+/).map((value) => value.trim()).filter(Boolean);
}

const contentColumns = ['客户问题', '用户问题', '消息内容', '客户消息', '用户消息', '买家消息', '会话内容', '聊天内容', '提问内容'];

function extractCsvConversations(source: string) {
  const parsed = Papa.parse<Record<string, string>>(source, { header: true, skipEmptyLines: true });
  const fields = parsed.meta.fields ?? [];
  const contentField = contentColumns.find((field) => fields.includes(field));
  const field = contentField ?? (fields.includes('会话意图') ? '会话意图' : null);
  if (!field) return { values: [] as string[], field: null, total: parsed.data.length, ignored: parsed.data.length };
  const genericIntent = new Set(['-', '无三级分类', '消费者准备提问', '未知', '其他']);
  const raw = parsed.data.map((row) => row[field]?.trim() ?? '');
  const values = raw.filter((value) => value && (contentField || !genericIntent.has(value)));
  return { values, field, total: parsed.data.length, ignored: raw.length - values.length };
}

export function KnowledgePage({ onMenuOpen }: { onMenuOpen: () => void }) {
  const [items, setItems] = useState<KnowledgeItem[]>([]);
  const [summary, setSummary] = useState<KnowledgeData['summary']>({ conversationCount: 0, lastAnalyzedAt: null, periodDays: 7 });
  const [view, setView] = useState<'manage' | 'search'>('manage');
  const [selectedId, setSelectedId] = useState('');
  const [filter, setFilter] = useState<'all' | ItemStatus>('all');
  const [listSearch, setListSearch] = useState('');
  const [instruction, setInstruction] = useState('');
  const [librarySearch, setLibrarySearch] = useState('你们发什么快递');
  const [manualOpen, setManualOpen] = useState(false);
  const [manualQuestion, setManualQuestion] = useState('');
  const [manualCategory, setManualCategory] = useState('其他');
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [analysisText, setAnalysisText] = useState('');
  const [analysisFile, setAnalysisFile] = useState('');
  const [analysisInfo, setAnalysisInfo] = useState<{ field: string; total: number; ignored: number } | null>(null);
  const [maxQuestions, setMaxQuestions] = useState(10);
  const [analyzing, setAnalyzing] = useState(false);
  const [generatingIds, setGeneratingIds] = useState<Set<string>>(new Set());
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    void getKnowledge().then((data) => {
      setItems(data.items);
      setSummary(data.summary);
      setSelectedId((current) => current || data.items.find((item) => item.status !== 'published')?.id || data.items[0]?.id || '');
    }).catch((error: Error) => setNotice(error.message));
  }, []);

  const selected = items.find((item) => item.id === selectedId) ?? items[0];
  const visibleItems = useMemo(() => items
    .filter((item) => filter === 'all' || item.status === filter)
    .filter((item) => !listSearch.trim() || `${item.question}${item.category}${item.aliases.join('')}`.includes(listSearch.trim()))
    .sort((a, b) => b.frequency - a.frequency), [filter, items, listSearch]);
  const published = items.filter((item) => item.status === 'published');
  const searchResults = useMemo(() => published
    .map((item) => ({ item, score: matchScore(item, librarySearch) }))
    .filter(({ score }) => !librarySearch.trim() || score >= 25)
    .sort((a, b) => b.score - a.score), [librarySearch, published]);

  const updateLocalItem = (id: string, update: Partial<KnowledgeItem>) => {
    setItems((current) => current.map((item) => item.id === id ? { ...item, ...update } : item));
  };

  const saveItem = async (id: string, update: Parameters<typeof updateKnowledgeItem>[1]) => {
    try {
      const saved = await updateKnowledgeItem(id, update);
      updateLocalItem(id, saved);
      return saved;
    } catch (error) {
      setNotice(error instanceof Error ? error.message : '保存失败');
      return null;
    }
  };

  const selectItem = (id: string) => {
    setSelectedId(id);
    if (window.innerWidth <= 800) {
      window.setTimeout(() => document.querySelector('.editorPanel')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    }
  };

  const generate = async (item: KnowledgeItem, customInstruction = instruction) => {
    setGeneratingIds((current) => new Set(current).add(item.id));
    try {
      const generated = await generateKnowledgeAnswer(item.id, customInstruction);
      updateLocalItem(item.id, generated);
      setInstruction('');
      setNotice(item.answer ? 'AI已按新方向重新优化话术' : '真实AI话术已生成，请审核');
      return generated;
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'AI生成失败');
      return null;
    } finally {
      setGeneratingIds((current) => { const next = new Set(current); next.delete(item.id); return next; });
    }
  };

  const batchGenerate = async () => {
    const pending = items.filter((item) => item.status === 'detected');
    if (pending.length === 0) return setNotice('没有等待生成的问题');
    const generated: Array<KnowledgeItem | null> = [];
    let cursor = 0;
    const worker = async () => {
      while (cursor < pending.length) {
        const item = pending[cursor++];
        if (item) generated.push(await generate(item, ''));
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, pending.length) }, () => worker()));
    setNotice(`AI已生成 ${generated.filter(Boolean).length} 条候选话术`);
  };

  const analyze = async () => {
    const conversations = parseConversations(analysisText);
    if (conversations.length === 0) return;
    setAnalyzing(true);
    try {
      const data = await analyzeConversations(conversations, maxQuestions, 7);
      setItems(data.items);
      setSummary(data.summary);
      setSelectedId(data.items[0]?.id ?? '');
      setAnalysisOpen(false);
      setNotice(`真实AI分析完成，共归纳 ${data.items.filter((item) => item.source === 'ai').length} 个问题`);
    } catch (error) {
      setNotice(error instanceof Error ? error.message : '对话分析失败');
    } finally {
      setAnalyzing(false);
    }
  };

  const addManual = async () => {
    if (!manualQuestion.trim()) return;
    try {
      const created = await createKnowledgeItem(manualQuestion.trim(), manualCategory);
      setItems((current) => [created, ...current]);
      setSelectedId(created.id);
      setManualQuestion('');
      setManualOpen(false);
      await generate(created, '生成准确、简洁、可以直接复制给客户的标准话术');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : '添加失败');
    }
  };

  const copy = async (answer: string) => {
    await navigator.clipboard.writeText(answer);
    setNotice('话术已复制，可以粘贴给客户');
  };

  return (
    <main className="main knowledgeMain">
      <header className="topbar knowledgeTopbar">
        <button className="menuButton" aria-label="打开导航" onClick={onMenuOpen}><Menu /></button>
        <strong>AI客服知识库</strong>
        <nav className="viewTabs" aria-label="知识库视图">
          <button className={view === 'manage' ? 'active' : ''} onClick={() => setView('manage')}><WandSparkles />主管工作台</button>
          <button className={view === 'search' ? 'active' : ''} onClick={() => setView('search')}><Search />客服查询</button>
        </nav>
        <span className="offlineBadge"><Archive />离线分析</span>
      </header>

      {view === 'manage' ? (
        <div className="knowledgeContent">
          <section className="knowledgeHero">
            <div><h1>高频问题与标准话术</h1><p>定期分析近期客户对话，审核AI生成的话术后发布给客服使用。</p></div>
            <div className="headActions">
              <button className="button" onClick={() => setManualOpen(true)}><Plus />手动添加</button>
              <button className="button primary" onClick={() => setAnalysisOpen(true)}><FileUp />导入并分析近期对话</button>
            </div>
          </section>

          <section className="knowledgeStats">
            <div><span>本期分析对话</span><strong>{summary.conversationCount.toLocaleString('zh-CN')}</strong><small>最近 {summary.periodDays} 天导入数据</small></div>
            <div><span>识别高频问题</span><strong>{items.filter((item) => item.source === 'ai').length}</strong><small>{summary.lastAnalyzedAt ? `最近更新：${new Intl.DateTimeFormat('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(summary.lastAnalyzedAt))}` : '尚未执行分析'}</small></div>
            <div><span>等待主管确认</span><strong>{items.filter((item) => item.status !== 'published').length}</strong><small>生成后可逐条优化</small></div>
            <div><span>已发布知识</span><strong>{published.length}</strong><small>客服可搜索使用</small></div>
          </section>

          <section className="knowledgeWorkspace">
            <aside className="questionPanel">
              <header><div><strong>问题列表</strong><span>{visibleItems.length} 条</span></div><button className="button small aiBatch" disabled={generatingIds.size > 0} onClick={() => void batchGenerate()}><Sparkles />{generatingIds.size > 0 ? `生成中 ${generatingIds.size}` : '一键生成全部'}</button></header>
              <label className="knowledgeListSearch"><Search /><input value={listSearch} onChange={(event) => setListSearch(event.target.value)} placeholder="搜索问题" /></label>
              <div className="statusFilters">
                {([['all', '全部'], ['detected', '待生成'], ['draft', '待确认'], ['published', '已入库']] as const).map(([value, label]) => <button key={value} className={filter === value ? 'active' : ''} onClick={() => setFilter(value)}>{label}<span>{value === 'all' ? items.length : items.filter((item) => item.status === value).length}</span></button>)}
              </div>
              <div className="questionList">
                {visibleItems.map((item) => (
                  <button key={item.id} className={selected?.id === item.id ? 'active' : ''} onClick={() => selectItem(item.id)}>
                    <div><span className={`statusDot ${item.status}`} /> <small>{statusLabel(item.status)}</small>{item.source === 'manual' && <em>人工</em>}</div>
                    <strong>{item.question}</strong>
                    <footer><span>{item.category}</span><span>{item.frequency ? `近7天 ${item.frequency} 次` : '未统计频次'}</span><ChevronRight /></footer>
                  </button>
                ))}
              </div>
            </aside>

            {selected && <section className="editorPanel">
              <header>
                <div><span className={`statusTag ${selected.status}`}>{statusLabel(selected.status)}</span><span>{selected.category}</span><span>{selected.platforms.join(' · ')}</span></div>
                <button className="iconButton deleteKnowledge" title="移除无效问题" onClick={() => void deleteKnowledgeItem(selected.id).then(() => {
                  setItems((current) => current.filter((item) => item.id !== selected.id));
                  setSelectedId(items.find((item) => item.id !== selected.id)?.id ?? '');
                  setNotice('无效问题已移除');
                }).catch((error: Error) => setNotice(error.message))}><Trash2 /></button>
              </header>

              <div className="editorBody">
                <section className="questionDetail">
                  <label>标准问题</label>
                  <h2>{selected.question}</h2>
                  {selected.aliases.length > 0 && <div className="aliasRow"><span>相似问法</span>{selected.aliases.map((alias) => <i key={alias}>{alias}</i>)}</div>}
                  {selected.frequency > 0 && <div className="frequencyRow"><strong>{selected.frequency}</strong><span>近7天出现次数</span><b className={selected.trend >= 0 ? 'up' : 'down'}>{selected.trend >= 0 ? '+' : ''}{selected.trend}%</b></div>}
                </section>

                <section className="answerEditor">
                  <div className="sectionTitle"><div><Sparkles /><strong>推荐话术</strong></div>{selected.versions > 0 && <span>已优化 {selected.versions} 次</span>}</div>
                  {selected.answer ? <textarea value={selected.answer} onChange={(event) => updateLocalItem(selected.id, { answer: event.target.value, status: 'draft' })} onBlur={(event) => void saveItem(selected.id, { answer: event.target.value, status: 'draft' })} aria-label="推荐话术" /> : <div className="answerEmpty"><MessageSquareText /><strong>还没有生成话术</strong><span>让真实AI根据这个问题生成一条可直接发送给客户的答案</span><button className="button primary" disabled={generatingIds.has(selected.id)} onClick={() => void generate(selected)}><Sparkles />{generatingIds.has(selected.id) ? '正在生成...' : '生成话术'}</button></div>}
                </section>

                {selected.answer && <section className="refineBox">
                  <label htmlFor="instruction">不满意？告诉AI怎么修改</label>
                  <div><input id="instruction" value={instruction} onChange={(event) => setInstruction(event.target.value)} placeholder="例如：更简短一些，语气更亲切，不要承诺具体时间" onKeyDown={(event) => { if (event.key === 'Enter') void generate(selected); }} /><button className="button" disabled={generatingIds.has(selected.id)} onClick={() => void generate(selected)}><RefreshCw className={generatingIds.has(selected.id) ? 'spin' : ''} />{generatingIds.has(selected.id) ? '优化中...' : '重新优化'}</button></div>
                  <div className="quickInstructions">{['更简短一些', '语气更亲切', '表达更明确'].map((value) => <button key={value} onClick={() => setInstruction(value)}>{value}</button>)}</div>
                </section>}
              </div>

              <footer className="editorFooter">
                <span>{selected.status === 'published' ? <><BookOpenCheck />这条话术已在客服端生效</> : '确认后，客服即可通过模糊搜索找到这条话术'}</span>
                {selected.status === 'published' ? <button className="button" onClick={() => void saveItem(selected.id, { status: 'draft' })}>撤回修改</button> : <button className="button primary" disabled={!selected.answer.trim()} onClick={() => void saveItem(selected.id, { answer: selected.answer, status: 'published' }).then((saved) => saved && setNotice('话术已发布到客服知识库'))}><Check />确认并入库</button>}
              </footer>
            </section>}
          </section>
        </div>
      ) : (
        <div className="agentKnowledge">
          <section className="agentSearchHead">
            <div className="agentSearchIcon"><BookOpenCheck /></div>
            <h1>客户问了什么？</h1>
            <p>输入客户的问题，系统会匹配已审核的标准话术。</p>
            <label><Search /><input autoFocus value={librarySearch} onChange={(event) => setLibrarySearch(event.target.value)} placeholder="例如：你们发什么快递？" /><button aria-label="清空搜索" onClick={() => setLibrarySearch('')}>清空</button></label>
          </section>
          <section className="searchResults">
            <header><strong>{librarySearch.trim() ? `找到 ${searchResults.length} 条相关话术` : `全部话术 ${published.length} 条`}</strong><span>只展示主管审核通过的内容</span></header>
            {searchResults.map(({ item }, index) => <article className={index === 0 && librarySearch.trim() ? 'bestMatch' : ''} key={item.id}>
              <div className="resultMeta">{index === 0 && librarySearch.trim() && <b><Sparkles />最佳匹配</b>}<span>{item.category}</span><span>{item.platforms.join(' · ')}</span></div>
              <h2>{item.question}</h2>
              <div className="answerCopy"><p>{item.answer}</p><button className="button primary" onClick={() => void copy(item.answer)}><Copy />复制话术</button></div>
              {item.aliases.length > 0 && <small>也可匹配：{item.aliases.join('、')}</small>}
            </article>)}
            {searchResults.length === 0 && <div className="noResult"><Search /><strong>暂时没有匹配的话术</strong><span>换一种说法搜索，或联系主管补充到知识库。</span></div>}
          </section>
        </div>
      )}

      {analysisOpen && <div className="modalLayer" role="presentation" onMouseDown={() => !analyzing && setAnalysisOpen(false)}>
        <section className="manualModal analysisModal" role="dialog" aria-modal="true" aria-labelledby="analysis-title" onMouseDown={(event) => event.stopPropagation()}>
          <header><div><FileUp /><span><strong id="analysis-title">导入近期客户对话</strong><small>支持 TXT、CSV、JSON，每行作为一段待分析内容</small></span></div><button className="iconButton" disabled={analyzing} onClick={() => setAnalysisOpen(false)}>×</button></header>
          <div>
            <label className="filePicker"><input type="file" accept=".txt,.csv,.json,text/plain,text/csv,application/json" onChange={(event) => {
              const file = event.target.files?.[0];
              if (!file) return;
              setAnalysisFile(file.name);
              void file.text().then((source) => {
                if (file.name.toLowerCase().endsWith('.csv')) {
                  const extracted = extractCsvConversations(source);
                  if (!extracted.field) {
                    setAnalysisText('');
                    setAnalysisInfo(null);
                    setNotice('CSV中没有找到客户问题、消息内容或会话意图字段');
                    return;
                  }
                  setAnalysisText(extracted.values.join('\n'));
                  setAnalysisInfo({ field: extracted.field, total: extracted.total, ignored: extracted.ignored });
                  return;
                }
                setAnalysisText(source);
                setAnalysisInfo({ field: '文本内容', total: parseConversations(source).length, ignored: 0 });
              });
            }} /><FileUp /><strong>{analysisFile || '选择对话文件'}</strong><span>或直接在下方粘贴内容</span></label>
            {analysisInfo && <div className="safeImportNotice"><Check /><span>已安全提取 <strong>{parseConversations(analysisText).length.toLocaleString('zh-CN')}</strong> 条“{analysisInfo.field}”，已过滤 {analysisInfo.ignored.toLocaleString('zh-CN')} 条空值或无效分类；用户信息和订单字段不会发送给AI。</span></div>}
            <label>对话内容<textarea value={analysisText} onChange={(event) => setAnalysisText(event.target.value)} placeholder={'客户：你们发什么快递？\n客户：什么时候可以发货？\n客户：这款适合油性发质吗？'} /></label>
            <div className="analysisOptions"><label>输出高频问题数量<input type="number" min="1" max="30" value={maxQuestions} onChange={(event) => setMaxQuestions(Number(event.target.value))} /></label><span>已读取 {parseConversations(analysisText).length} 段内容</span></div>
          </div>
          <footer><button className="button" disabled={analyzing} onClick={() => setAnalysisOpen(false)}>取消</button><button className="button primary" disabled={analyzing || parseConversations(analysisText).length === 0} onClick={() => void analyze()}><Sparkles />{analyzing ? 'AI正在归类分析...' : '开始AI分析'}</button></footer>
        </section>
      </div>}

      {manualOpen && <div className="modalLayer" role="presentation" onMouseDown={() => setManualOpen(false)}>
        <section className="manualModal" role="dialog" aria-modal="true" aria-labelledby="manual-title" onMouseDown={(event) => event.stopPropagation()}>
          <header><div><FilePlus2 /><span><strong id="manual-title">手动添加典型问题</strong><small>AI没有识别到的问题也可以单独加入</small></span></div><button className="iconButton" onClick={() => setManualOpen(false)}>×</button></header>
          <div><label>客户问题<textarea autoFocus value={manualQuestion} onChange={(event) => setManualQuestion(event.target.value)} placeholder="输入客户经常问或有代表性的问题" /></label><label>问题分类<select value={manualCategory} onChange={(event) => setManualCategory(event.target.value)}>{['产品咨询', '产品使用', '物流配送', '订单问题', '售后服务', '活动优惠', '其他'].map((category) => <option key={category}>{category}</option>)}</select></label></div>
          <footer><button className="button" onClick={() => setManualOpen(false)}>取消</button><button className="button primary" disabled={!manualQuestion.trim()} onClick={() => void addManual()}><Plus />添加并生成话术</button></footer>
        </section>
      </div>}
      {notice && <button className="toast" onClick={() => setNotice(null)}>{notice}</button>}
    </main>
  );
}
